/**
 * Export the wheel itself as a PNG.
 *
 * Distinct from winnerCard.ts, which draws a *result* card — the winner's name
 * on a branded background, made for sharing an outcome. This exports the wheel
 * as it currently stands, which is what someone wants for a different set of
 * reasons: printing a rota for a classroom wall, evidencing a prize draw, or
 * putting the actual options into a slide.
 *
 * The wheel canvas is already rendered at devicePixelRatio, so on a retina
 * screen the export comes out at roughly double the CSS size. That is left
 * alone deliberately — a wheel destined for print wants every pixel it can get,
 * and downscaling here would throw away resolution to save a few hundred KB.
 */

/** Layout constants in the wheel's 600-unit space; scaled to the source canvas at draw time. */
const PAD_BASE = 48;
const TITLE_H_BASE = 96;
const FOOTER_H_BASE = 56;

/** Filename-safe slug, matching the convention in winnerCard.ts. */
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'wheel';
}

/**
 * Compose the wheel onto a titled card.
 *
 * Returns null when the wheel has not rendered yet, rather than producing a
 * blank image — a downloaded empty PNG looks like a bug the user caused.
 */
export function drawWheelImage(
  source: HTMLCanvasElement | null,
  wheelTitle: string,
  entryCount: number,
): HTMLCanvasElement | null {
  if (!source || source.width === 0 || source.height === 0) return null;

  // The wheel canvas can be any size (it fills its container), so the margins
  // scale with it — fixed pixel margins would let a large wheel run into the
  // title and footer text, which is itself scaled.
  const scale = source.width / 600;
  const PAD = Math.round(PAD_BASE * scale);
  const TITLE_H = Math.round(TITLE_H_BASE * scale);
  const FOOTER_H = Math.round(FOOTER_H_BASE * scale);

  const w = source.width + PAD * 2;
  const h = source.height + PAD * 2 + TITLE_H + FOOTER_H;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;

  // Opaque background. The wheel canvas is transparent outside the disc, and a
  // transparent PNG dropped into a document or printed comes out with whatever
  // is behind it — usually black in a slide deck.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);

  // Brand rule along the top, matching the Open Graph cards.
  ctx.fillStyle = '#7c3aed';
  ctx.fillRect(0, 0, w, Math.max(8, Math.round(8 * scale)));

  ctx.textAlign = 'center';

  ctx.fillStyle = '#111827';
  ctx.font = `bold ${Math.round(34 * scale)}px 'Inter Variable', Inter, system-ui, sans-serif`;
  ctx.fillText(wheelTitle || 'My Wheel', w / 2, PAD + Math.round(44 * scale));

  ctx.fillStyle = '#6b7280';
  ctx.font = `${Math.round(19 * scale)}px 'Inter Variable', Inter, system-ui, sans-serif`;
  ctx.fillText(
    `${entryCount} ${entryCount === 1 ? 'entry' : 'entries'}`,
    w / 2,
    PAD + Math.round(76 * scale),
  );

  ctx.drawImage(source, PAD, PAD + TITLE_H);

  ctx.fillStyle = '#9ca3af';
  ctx.font = `${Math.round(17 * scale)}px 'Inter Variable', Inter, system-ui, sans-serif`;
  ctx.fillText('thenamewheel.com', w / 2, h - Math.round(22 * scale));

  return out;
}

/** Download the wheel as a PNG. Returns false when there was nothing to draw. */
export function downloadWheelImage(
  source: HTMLCanvasElement | null,
  wheelTitle: string,
  entryCount: number,
): boolean {
  const canvas = drawWheelImage(source, wheelTitle, entryCount);
  if (!canvas) return false;

  const url = canvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug(wheelTitle)}-wheel.png`;
  a.click();
  return true;
}

/**
 * Share the wheel image through the OS share sheet, falling back to a download.
 *
 * Mirrors shareWinner(): an AbortError means the user dismissed the sheet,
 * which is a completed interaction rather than a failure, so it must NOT fall
 * through to a download — that would hand them a file they just declined.
 */
export async function shareWheelImage(
  source: HTMLCanvasElement | null,
  wheelTitle: string,
  entryCount: number,
): Promise<'shared' | 'downloaded' | 'cancelled' | 'unavailable'> {
  const canvas = drawWheelImage(source, wheelTitle, entryCount);
  if (!canvas) return 'unavailable';

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(b => resolve(b), 'image/png'),
  );
  if (!blob) return 'unavailable';

  const file = new File([blob], `${slug(wheelTitle)}-wheel.png`, { type: 'image/png' });
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };

  if (nav.canShare?.({ files: [file] }) && nav.share) {
    try {
      await nav.share({ files: [file], title: wheelTitle });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return 'cancelled';
      // Any other failure is a real one; a download still gets them the image.
    }
  }

  downloadWheelImage(source, wheelTitle, entryCount);
  return 'downloaded';
}
